(() => {
  const minimumLoadingTime = 420;

  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  const parsePayload = (text) => {
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (error) {
      throw new Error('Shopify returned an invalid cart response.');
    }
  };

  const responseError = (payload, fallback) => new Error(
    payload.description || payload.message || fallback
  );

  const requestJson = (url, options = {}) => new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(options.method || 'GET', url, true);
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    Object.entries(options.headers || {}).forEach(([name, value]) => request.setRequestHeader(name, value));
    request.onload = () => {
      try {
        const payload = parsePayload(request.responseText);
        if (request.status < 200 || request.status >= 300) {
          reject(responseError(payload, `Cart request failed (${request.status}).`));
          return;
        }
        resolve(payload);
      } catch (error) {
        reject(error);
      }
    };
    request.onerror = () => reject(new Error('Unable to connect to the Shopify cart.'));
    request.ontimeout = () => reject(new Error('The Shopify cart request timed out.'));
    request.timeout = 15000;
    request.send(options.body || null);
  });

  class SourceProductAddToCart {
    constructor(product) {
      this.product = product;
      this.form = product.querySelector('[data-spx-cart-form]');
      this.button = this.form?.querySelector('[data-spx-cart-button]');
      this.variantInput = this.form?.querySelector('[data-spx-cart-variant-id]');
      this.error = this.form?.querySelector('[data-spx-cart-error]');
      this.addUrl = this.form?.dataset.spxCartAddUrl;
      this.cartUrl = this.form?.dataset.spxCartUrl;
      this.variantAvailable = !this.button?.disabled;
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connect() {
      if (!this.form || !this.button || !this.variantInput || !this.addUrl || !this.cartUrl) return;
      this.form.addEventListener('submit', this.onSubmit);
      this.product.addEventListener('spx:variant-change', this.onVariantChange);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant;
      this.variantInput.value = variant?.id || '';
      this.variantAvailable = Boolean(variant?.available);
      this.button.disabled = this.button.classList.contains('is-loading') || !this.variantAvailable;
    }

    buildAddRequest() {
      const formData = new FormData(this.form);
      const addonToggle = this.form.querySelector('[data-spx-addon-toggle]');
      const addonDefault = this.form.querySelector('[data-spx-addon-default="true"]');
      const addonInput = addonToggle || addonDefault;
      const addonEnabled = addonToggle ? addonToggle.checked : Boolean(addonDefault);
      const rawAddonIds = String(addonInput?.dataset.spxAddonVariantId || '');
      const addonIds = rawAddonIds
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isInteger(n) && n > 0);

      if (!addonEnabled || addonIds.length === 0) {
        return { body: formData };
      }

      const properties = {};
      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch) properties[propertyMatch[1]] = value;
      });

      const qty = Number(formData.get('quantity')) || 1;
      const primaryItem = {
        id: Number(this.variantInput.value),
        quantity: qty
      };
      if (Object.keys(properties).length) primaryItem.properties = properties;

      const items = [primaryItem, ...addonIds.map((id) => ({ id, quantity: qty }))];

      return {
        body: JSON.stringify({ items }),
        headers: { 'Content-Type': 'application/json' }
      };
    }

    setLoading(loading) {
      this.button.disabled = loading || !this.variantAvailable;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    publish(status, detail = {}) {
      document.dispatchEvent(new CustomEvent('spx:add-to-cart', {
        detail: { status, variantId: Number(this.variantInput.value), ...detail }
      }));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.button.classList.contains('is-loading') || !this.variantInput.value) return;

      this.showError();
      this.setLoading(true);
      this.publish('started');

      try {
        const addRequestOptions = this.buildAddRequest();
        const addRequest = requestJson(this.addUrl, {
          method: 'POST',
          ...addRequestOptions
        });
        const [added] = await Promise.all([addRequest, wait(minimumLoadingTime)]);
        const cart = await requestJson(this.cartUrl);

        document.dispatchEvent(new CustomEvent('spx:cart-added', {
          detail: { cart, added, source: this.button }
        }));
        if (window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        }
        this.publish('succeeded', { cart, added });
      } catch (error) {
        this.showError(error.message);
        this.publish('failed', { message: error.message, addUrl: this.addUrl, cartUrl: this.cartUrl });
      } finally {
        this.setLoading(false);
      }
    }
  }

  const initialize = () => {
    document.querySelectorAll('[data-spx-product]').forEach((product) => {
      if (product.dataset.spxCartInitialized === 'true') return;
      product.dataset.spxCartInitialized = 'true';
      new SourceProductAddToCart(product).connect();
    });
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initialize, { once: true })
    : initialize();
})();
